import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { PLAYER_COLORS } from '../../../utils/constants.js';
import { PALETTE, matte, colored, vary, seededRandom, jitter } from '../../Style.js';

// Undyed cloth for unclaimed towers
const NEUTRAL_CLOTH = PALETTE.cloth;
const NEUTRAL_RING = 0x9a9486;

const DECK_HEIGHT = 1.8;
const POST_RADIUS = 2.2;

// A ballista watchtower: a coursed stone drum with a timber deck, corner
// pennants and hanging banners in the owner's colours, and a ballista on a
// turntable that tracks targets.
export class Turret {
  constructor(scene, turret) {
    this.scene = scene;
    this.turret = turret;
    this.meshGroup = new THREE.Group();
    this.headGroup = null; // Rotating part (turntable + ballista)
    this.baseMesh = null;
    this.headMesh = null; // Owner-coloured shield on the ballista, pulses while tracking
    this.gunMesh = null;
    this.rangeMesh = null;
    this.teamMaterial = null;
    this.pennants = [];
    this.targetRotation = 0;
    this.currentRotation = 0;
    this.rand = seededRandom(hashId(turret.id));
    this.create();
  }

  create() {
    const color = this.getColor();

    this.teamMaterial = new THREE.MeshStandardMaterial({
      color: this.getClothColor(),
      roughness: 0.9,
      flatShading: true,
      side: THREE.DoubleSide,
      emissive: color,
      emissiveIntensity: this.turret.ownerId === -1 ? 0 : 0.15
    });

    // Tower: stone footing and drum, timber deck (stationary)
    this.baseMesh = new THREE.Mesh(mergeGeometries(this.buildTower()), matte(0xffffff, { vertexColors: true }));
    this.baseMesh.castShadow = true;
    this.baseMesh.receiveShadow = true;
    this.meshGroup.add(this.baseMesh);

    // Banners hanging down the four faces of the drum
    const banners = [];
    for (let i = 0; i < 4; i++) {
      const banner = new THREE.ShapeGeometry(bannerShape(0.9, 1.15));
      banner.translate(0, DECK_HEIGHT - 0.25, 2.36);
      banner.rotateY(i * Math.PI / 2);
      banners.push(banner);
    }
    const bannerMesh = new THREE.Mesh(mergeGeometries(banners), this.teamMaterial);
    bannerMesh.castShadow = true;
    this.meshGroup.add(bannerMesh);

    // Pennants on the corner posts, swaying in the wind
    for (let i = 0; i < 4; i++) {
      const a = Math.PI / 4 + i * Math.PI / 2;
      const pennant = new THREE.Mesh(new THREE.ShapeGeometry(pennantShape(1.0, 0.5)), this.teamMaterial);
      pennant.position.set(Math.sin(a) * POST_RADIUS, DECK_HEIGHT + 1.45, Math.cos(a) * POST_RADIUS);
      pennant.userData.phase = i * 1.7;
      pennant.castShadow = true;
      this.meshGroup.add(pennant);
      this.pennants.push(pennant);
    }

    // Rotating head: turntable and ballista, aimed along +Z
    this.headGroup = new THREE.Group();
    this.headGroup.position.y = DECK_HEIGHT;
    this.meshGroup.add(this.headGroup);

    const ballista = new THREE.Mesh(mergeGeometries(this.buildBallista()), matte(0xffffff, { vertexColors: true }));
    ballista.castShadow = true;
    this.headGroup.add(ballista);

    // The loaded bolt
    this.gunMesh = new THREE.Mesh(mergeGeometries(this.buildBolt()), matte(0xffffff, { vertexColors: true }));
    this.gunMesh.castShadow = true;
    this.headGroup.add(this.gunMesh);

    // Owner shield on the front of the stock
    const shieldMaterial = new THREE.MeshStandardMaterial({
      color: this.getClothColor(),
      roughness: 0.85,
      flatShading: true,
      emissive: color,
      emissiveIntensity: 0.2
    });
    this.headMesh = new THREE.Mesh(new THREE.CylinderGeometry(0.45, 0.45, 0.1, 6), shieldMaterial);
    this.headMesh.rotation.x = Math.PI / 2;
    this.headMesh.position.set(0, 0.62, 1.95);
    this.headMesh.castShadow = true;
    this.headGroup.add(this.headMesh);

    // Range indicator (semi-transparent ring)
    const rangeGeometry = new THREE.RingGeometry(
      this.turret.claimRadius - 0.3,
      this.turret.claimRadius,
      32
    );
    const rangeMaterial = new THREE.MeshBasicMaterial({
      color: this.getRingColor(),
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.3
    });
    this.rangeMesh = new THREE.Mesh(rangeGeometry, rangeMaterial);
    this.rangeMesh.rotation.x = -Math.PI / 2;
    this.rangeMesh.position.y = 0.05;
    this.meshGroup.add(this.rangeMesh);

    // Position the group
    this.meshGroup.position.set(
      this.turret.position.x,
      this.turret.position.y,
      this.turret.position.z
    );

    this.scene.add(this.meshGroup);
  }

  buildTower() {
    const rand = this.rand;
    const parts = [];

    // Rough footing stones
    parts.push(colored(
      jitter(new THREE.CylinderGeometry(2.35, 2.55, 0.5, 8), 0.12, rand),
      PALETTE.stoneDark,
      { y: 0.25, ry: Math.PI / 8 }
    ));

    // Mortar core of the drum
    parts.push(colored(new THREE.CylinderGeometry(1.95, 2.15, 1.2, 8), PALETTE.stoneDark, { y: 1.1, ry: Math.PI / 8 }));

    // Two courses of dressed blocks around the drum, joints staggered
    for (let row = 0; row < 2; row++) {
      const count = 10;
      const radius = 2.08 - row * 0.08;
      for (let i = 0; i < count; i++) {
        const a = ((i + row * 0.5) / count) * Math.PI * 2;
        const color = rand() < 0.15 ? vary(PALETTE.mossStone, rand) : vary(PALETTE.stone, rand, 0.14, 0.02);
        parts.push(colored(
          new THREE.BoxGeometry(1.22, 0.56, 0.4 + rand() * 0.08),
          color,
          { x: Math.sin(a) * radius, y: 0.78 + row * 0.6, z: Math.cos(a) * radius, ry: a }
        ));
      }
    }

    // Cross beams carrying the deck, with ends poking out
    for (let i = 0; i < 2; i++) {
      parts.push(colored(new THREE.BoxGeometry(5.0, 0.24, 0.3), PALETTE.woodDark, { y: DECK_HEIGHT - 0.32, ry: i * Math.PI / 2 }));
    }

    // Plank deck
    parts.push(colored(new THREE.CylinderGeometry(2.45, 2.45, 0.2, 8), PALETTE.wood, { y: DECK_HEIGHT - 0.1, ry: Math.PI / 8 }));
    for (let i = -2; i <= 2; i++) {
      parts.push(colored(
        new THREE.BoxGeometry(0.06, 0.02, 4.2 - Math.abs(i) * 0.6),
        PALETTE.woodDark,
        { x: i * 0.85, y: DECK_HEIGHT + 0.01 }
      ));
    }

    // Corner posts with iron caps
    for (let i = 0; i < 4; i++) {
      const a = Math.PI / 4 + i * Math.PI / 2;
      const x = Math.sin(a) * POST_RADIUS;
      const z = Math.cos(a) * POST_RADIUS;
      parts.push(colored(new THREE.CylinderGeometry(0.1, 0.13, 1.7, 5), PALETTE.woodDark, { x, y: DECK_HEIGHT + 0.85, z }));
      parts.push(colored(new THREE.ConeGeometry(0.16, 0.25, 5), PALETTE.iron, { x, y: DECK_HEIGHT + 1.82, z }));
    }

    // Rods the banners hang from
    for (let i = 0; i < 4; i++) {
      parts.push(colored(new THREE.CylinderGeometry(0.05, 0.05, 1.1, 4), PALETTE.woodDark, {
        rz: Math.PI / 2, y: DECK_HEIGHT - 0.24, z: 2.38
      }).rotateY(i * Math.PI / 2));
    }

    return parts;
  }

  buildBallista() {
    const parts = [];
    const v = (x, y, z) => new THREE.Vector3(x, y, z);

    // Turntable and pedestal
    parts.push(colored(new THREE.CylinderGeometry(0.95, 1.0, 0.18, 8), PALETTE.woodDark, { y: 0.09 }));
    parts.push(colored(new THREE.CylinderGeometry(0.98, 0.98, 0.06, 8), PALETTE.iron, { y: 0.2 }));
    parts.push(colored(new THREE.BoxGeometry(0.45, 0.6, 0.6), PALETTE.wood, { y: 0.5 }));

    // Stock with the bolt track
    parts.push(colored(new THREE.BoxGeometry(0.36, 0.28, 3.0), PALETTE.woodLight, { y: 0.9, z: 0.4 }));
    parts.push(colored(new THREE.BoxGeometry(0.42, 0.1, 0.5), PALETTE.iron, { y: 0.86, z: 1.7 }));

    // Bow arms sweeping back, with iron tips
    const tips = [];
    for (const side of [-1, 1]) {
      const root = v(0.15 * side, 0.95, 1.7);
      const tip = v(1.5 * side, 0.98, 1.15);
      parts.push(beam(root, tip, 0.17, PALETTE.woodDark));
      parts.push(colored(new THREE.BoxGeometry(0.22, 0.22, 0.22), PALETTE.iron, { x: tip.x, y: tip.y, z: tip.z }));
      tips.push(tip);
    }

    // Bowstring drawn back to the claw
    const claw = v(0, 1.08, -0.15);
    for (const tip of tips) {
      parts.push(beam(tip, claw, 0.04, PALETTE.cloth));
    }
    parts.push(colored(new THREE.BoxGeometry(0.2, 0.14, 0.2), PALETTE.iron, { y: 1.08, z: -0.15 }));

    // Winch at the rear
    parts.push(colored(new THREE.CylinderGeometry(0.14, 0.14, 0.8, 6), PALETTE.wood, { rz: Math.PI / 2, y: 0.85, z: -0.95 }));
    for (const side of [-1, 1]) {
      parts.push(colored(new THREE.BoxGeometry(0.06, 0.5, 0.06), PALETTE.woodDark, { x: 0.43 * side, y: 0.85, z: -0.95 }));
    }

    return parts;
  }

  buildBolt() {
    return [
      colored(new THREE.CylinderGeometry(0.05, 0.05, 2.1, 5), PALETTE.woodLight, { rx: Math.PI / 2, y: 1.12, z: 0.95 }),
      colored(new THREE.ConeGeometry(0.11, 0.35, 4), PALETTE.iron, { rx: Math.PI / 2, y: 1.12, z: 2.15 }),
      colored(new THREE.BoxGeometry(0.02, 0.18, 0.35), PALETTE.cloth, { y: 1.18, z: 0.05 }),
      colored(new THREE.BoxGeometry(0.18, 0.02, 0.35), PALETTE.cloth, { y: 1.12, z: 0.05 })
    ];
  }

  getColor() {
    if (this.turret.ownerId === -1) {
      return 0x888888; // Gray for unclaimed
    }
    // Parse hex color string to number
    const colorStr = PLAYER_COLORS[this.turret.ownerId];
    return parseInt(colorStr.replace('#', ''), 16);
  }

  getClothColor() {
    return this.turret.ownerId === -1 ? NEUTRAL_CLOTH : this.getColor();
  }

  getRingColor() {
    return this.turret.ownerId === -1 ? NEUTRAL_RING : this.getColor();
  }

  update(turretData, time, units = []) {
    const ownerChanged = !this.turret || this.turret.ownerId !== turretData.ownerId;
    this.turret = turretData;

    // Re-dye the cloth if the owner changed
    if (ownerChanged) {
      const color = this.getColor();
      const cloth = this.getClothColor();
      const neutral = this.turret.ownerId === -1;
      this.teamMaterial.color.setHex(cloth);
      this.teamMaterial.emissive.setHex(color);
      this.teamMaterial.emissiveIntensity = neutral ? 0 : 0.15;
      if (this.headMesh) {
        this.headMesh.material.color.setHex(cloth);
        this.headMesh.material.emissive.setHex(color);
      }
      if (this.rangeMesh) {
        this.rangeMesh.material.color.setHex(this.getRingColor());
      }
    }

    // Handle destroyed state
    if (this.turret.isDestroyed) {
      this.meshGroup.visible = false;
    } else {
      this.meshGroup.visible = true;

      // Pennants flutter
      for (const pennant of this.pennants) {
        const p = pennant.userData.phase;
        pennant.rotation.y = 0.6 + Math.sin(time * 2.2 + p) * 0.35;
        pennant.rotation.z = Math.sin(time * 3.1 + p) * 0.08;
      }

      // Find nearest enemy and rotate toward them
      if (this.turret.ownerId !== -1 && this.headGroup) {
        const nearestEnemy = this.findNearestEnemy(units);
        if (nearestEnemy) {
          // Calculate angle to enemy
          const dx = nearestEnemy.position.x - this.turret.position.x;
          const dz = nearestEnemy.position.z - this.turret.position.z;
          this.targetRotation = Math.atan2(dx, dz);
        }

        // Smoothly interpolate rotation
        let rotationDiff = this.targetRotation - this.currentRotation;
        // Normalize to [-PI, PI]
        while (rotationDiff > Math.PI) rotationDiff -= Math.PI * 2;
        while (rotationDiff < -Math.PI) rotationDiff += Math.PI * 2;
        this.currentRotation += rotationDiff * 0.1;

        this.headGroup.rotation.y = this.currentRotation;

        // Visual feedback for tracking state
        if (this.headMesh && this.turret.isTracking) {
          // Pulse emissive intensity while tracking, brighter as it locks on
          const baseIntensity = 0.2;
          const trackingPulse = Math.sin(time * 8) * 0.15; // Fast pulse while tracking
          const lockOnBoost = this.turret.trackingProgress * 0.6; // Gets brighter as it locks
          this.headMesh.material.emissiveIntensity = baseIntensity + trackingPulse + lockOnBoost;
          // Draw the bolt back as it locks on
          if (this.gunMesh) this.gunMesh.position.z = -this.turret.trackingProgress * 0.3;
        } else if (this.headMesh) {
          // Normal emissive when not tracking
          this.headMesh.material.emissiveIntensity = 0.2;
          if (this.gunMesh) this.gunMesh.position.z = 0;
        }
      } else if (this.turret.ownerId === -1 && this.headGroup) {
        // Slow idle rotation for unclaimed turrets
        this.headGroup.rotation.y = time * 0.2;
        if (this.headMesh) this.headMesh.material.emissiveIntensity = 0;
      }

      // Pulse effect for unclaimed turrets
      if (this.turret.ownerId === -1 && this.rangeMesh) {
        const pulse = 0.2 + Math.sin(time * 2) * 0.1;
        this.rangeMesh.material.opacity = pulse;
      } else if (this.rangeMesh) {
        this.rangeMesh.material.opacity = 0.3;
      }
    }
  }

  // Find nearest enemy unit within attack range
  findNearestEnemy(units) {
    if (!units || units.length === 0) return null;

    const attackRange = 20; // Match server TurretAttackRange (~4 squares)
    let nearest = null;
    let nearestDist = attackRange + 1;

    for (const unit of units) {
      // Only target enemy units
      if (unit.ownerId === this.turret.ownerId) continue;
      // Skip dead/respawning units
      if (unit.isRespawning) continue;

      const dx = unit.position.x - this.turret.position.x;
      const dz = unit.position.z - this.turret.position.z;
      const dist = Math.sqrt(dx * dx + dz * dz);

      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = unit;
      }
    }

    return nearest;
  }

  // Check if player is in claiming range
  isInRange(position) {
    const dx = position.x - this.turret.position.x;
    const dz = position.z - this.turret.position.z;
    const distSq = dx * dx + dz * dz;
    return distSq <= this.turret.claimRadius * this.turret.claimRadius;
  }

  // Check if turret can be claimed by player
  canBeClaimed(playerId) {
    if (this.turret.isDestroyed) return false;
    if (this.turret.ownerId === playerId) return false;
    return true;
  }

  remove() {
    this.scene.remove(this.meshGroup);
    this.meshGroup.traverse(child => {
      if (child.isMesh) child.geometry.dispose();
    });
    this.teamMaterial.dispose();
    this.headMesh.material.dispose();
    this.rangeMesh.material.dispose();
  }
}

// A box stretched between two points, for struts, arms and strings.
function beam(from, to, thickness, color) {
  const dir = new THREE.Vector3().subVectors(to, from);
  const geo = colored(new THREE.BoxGeometry(thickness, thickness, dir.length()), color);
  geo.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.normalize()));
  const mid = new THREE.Vector3().addVectors(from, to).multiplyScalar(0.5);
  geo.translate(mid.x, mid.y, mid.z);
  return geo;
}

// A hanging banner with a swallowtail hem, top edge at y = 0.
function bannerShape(width, height) {
  const w = width / 2;
  const shape = new THREE.Shape();
  shape.moveTo(-w, 0);
  shape.lineTo(w, 0);
  shape.lineTo(w, -height);
  shape.lineTo(0, -height + 0.3);
  shape.lineTo(-w, -height);
  shape.closePath();
  return shape;
}

// A triangular pennant flying along +X from its pole.
function pennantShape(length, height) {
  const shape = new THREE.Shape();
  shape.moveTo(0, 0);
  shape.lineTo(length, -height / 2);
  shape.lineTo(0, -height);
  shape.closePath();
  return shape;
}

function hashId(id) {
  let h = 2166136261;
  const str = String(id);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
